import { exportCSV, parseSize } from '@utils/index';
import {
  VariationType,
  SizeMapType,
  VariantInfoMapType,
  SizeOptions
} from '@types';

const sizeOrder = [
  'XX-Small',
  'X-Small',
  'Small',
  'Medium',
  'Large',
  'X-Large',
  'XX-Large',
  'XX-Large Big',
  'XXX-Large',
  'XXXX-Large',
  '1X',
  '2X',
  '3X',
  '3 X-Large',
  '3 X-Large Big',
  '4X',
  '4 X-Large',
  '4 X-Large Big',
  '5X',
  '5 X-Large',
  '5 X-Large Big',
  '6X',
  '5 X-Large',
  '5 X-Large Big',
  '7X'
];

export const ALL_SIZE_FILTER = { value: 'ALL', label: 'Show all sizes' };

const sortSizes = (sizes: string[]) => {
  return sizes.sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b));
};

export const getVariantInfo = (variationsToRender: Array<VariationType>) => {
  const variantInfoMap: VariantInfoMapType = {
    sizes: [],
    sizeMaps: [],
    sizeOptions: []
  };

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < variationsToRender.length; i++) {
    if (variationsToRender[i].variants?.size_name) {
      const size = variationsToRender[i].variants?.size_name;
      let sizeMapValue: SizeMapType = {
        size,
        lowest: variationsToRender[i].amount / 100,
        highest: variationsToRender[i].amount / 100,
        numItems: 1
      };

      if (!variantInfoMap.sizes.includes(size)) {
        variantInfoMap.sizes.push(size);
        variantInfoMap.sizeMaps.push(sizeMapValue);
      } else {
        // eslint-disable-next-line prefer-destructuring
        sizeMapValue = variantInfoMap.sizeMaps.filter(
          (sizeMap: SizeMapType) => {
            return sizeMap.size === size;
          }
        )[0];

        sizeMapValue.lowest = Math.min(
          variationsToRender[i].amount / 100,
          sizeMapValue.lowest
        );
        sizeMapValue.highest = Math.max(
          variationsToRender[i].amount / 100,
          sizeMapValue.highest
        );

        sizeMapValue.numItems += 1;
      }
    }
  }

  // Don't show just one size
  if (variantInfoMap.sizes.length === 1) {
    variantInfoMap.sizes = [];
  } else if (
    variantInfoMap.sizes.every(
      (i: string) => !Number.isNaN(Number(i.replace(/[^0-9.]/g, '')))
    )
  ) {
    // Sort the dropdown by size
    variantInfoMap.sizes.sort((a, b) => {
      return (
        Number(a.replace(/[^0-9.]/g, '')) - Number(b.replace(/[^0-9.]/g, ''))
      );
    });
  } else {
    variantInfoMap.sizes.sort();
  }

  const sizeFilterOptions = variantInfoMap.sizes;
  const sizeOptions: SizeOptions = [];

  const newSizeFilterOptions = sortSizes(variantInfoMap.sizes);
  const { sizeMaps } = variantInfoMap;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < newSizeFilterOptions.length; i++) {
    const sizeMap = sizeMaps.filter((sm) => {
      return sm.size === newSizeFilterOptions[i];
    })[0];

    const sizeMapStr =
      sizeMap.highest === sizeMap.lowest
        ? `($${sizeMap.highest.toFixed(2)})`
        : `($${sizeMap.lowest.toFixed(2)} - $${sizeMap.highest.toFixed(2)})`;

    sizeOptions.push({
      value: sizeFilterOptions[i],
      label: `${parseSize(sizeFilterOptions[i])} — ${sizeMapStr}`
    });
  }

  if (sizeOptions.length > 0) {
    sizeOptions.unshift(ALL_SIZE_FILTER);
  }

  variantInfoMap.sizeOptions = sizeOptions;

  return variantInfoMap;
};

const getExportFields = (additionalFields: string[]) => {
  return [
    'ASIN',
    'Parent ASIN',
    'Price',
    'List Price',
    'Title',
    'Image',
    'Prime Shipping',
    ...additionalFields
  ];
};

const variationToColumn = (
  d: VariationType,
  atLeastOneVariantHasColorName: boolean,
  atLeastOneVariantHasSizeName: boolean
) => {
  const additionalColumns = [];

  if (atLeastOneVariantHasColorName) {
    additionalColumns.push(d?.variants?.color_name || '');
  }

  if (atLeastOneVariantHasSizeName) {
    additionalColumns.push(d?.variants?.size_name || '');
  }

  return [
    d.ASIN,
    d.parentASIN,
    (d.amount / 100)?.toFixed(2) || '',
    d.listPrice?.toFixed(2) || '',
    d.title,
    d.image,
    d.prime === 1 ? 'Yes' : 'No',
    ...additionalColumns
  ];
};

export const exportVariations = (
  variations: Array<VariationType>,
  fileName: string
) => {
  const atLeastOneVariantHasColorName = variations.some(
    (v) => v?.variants?.color_name
  );

  const atLeastOneVariantHasSizeName = variations.some(
    (v) => v?.variants?.color_name
  );

  const additionalFields = [];
  if (atLeastOneVariantHasColorName) {
    additionalFields.push('Color Name');
  }
  if (atLeastOneVariantHasSizeName) {
    additionalFields.push('Size Name');
  }

  exportCSV(
    getExportFields(additionalFields),
    variations.map((d) => {
      return variationToColumn(
        d,
        atLeastOneVariantHasColorName,
        atLeastOneVariantHasSizeName
      );
    }) as unknown as object[][],
    fileName
  );
};
